import { CarrotPlanMediaItem, DisplayStatus } from "../../../types/CarrotPlanOptions"
import { buildMediaLink, buildSubCategoryLink } from "./contentfulLinkBuilder"
import {
  CarrotPlanFormOption,
  MediaOptionFields,
  MenuItemOptionFields,
  TopicOptionFields
} from "../hooks/useCarrotPlanMappings"
import { carrotPlanMediaMenuItemIds, getCarrotPlanMediaTypeIdForMenuItemId } from "../MediaTypes"

// Helper: filters out media items that should be hidden OR that have the incorrect media type
export function getFilteredMediaItems(mediaItems: CarrotPlanMediaItem[], mediaTypeId: string) {
  const mediaTypeIsCorrect = (mediaItem: CarrotPlanMediaItem): boolean => mediaItem.contentType === mediaTypeId
  const contentShouldBeHidden = (mediaItem: CarrotPlanMediaItem): boolean =>
    [DisplayStatus.ARCHIVED.toString(), DisplayStatus.REMOVE.toString(), DisplayStatus.UNASSIGNED.toString()].includes(
      mediaItem.optionMetadata.displayStatus
    )
  return mediaItems.filter((mediaItem) => mediaTypeIsCorrect(mediaItem) && !contentShouldBeHidden(mediaItem))
}

function getMandatoryMenuItemsByFlag(
  menuItems: CarrotPlanFormOption<MenuItemOptionFields>[] | null | undefined,
  flag: "isRequired" | "isPrioritized"
): CarrotPlanFormOption<MenuItemOptionFields>[] {
  return (menuItems ?? []).filter(
    (item) => item.optionMetadata?.[flag] === true && item.optionMetadata?.displayStatus !== DisplayStatus.DISABLE
  )
}

// Helper: filters menu item options to the required ones that are not DISABLED.
export function getOnlyRequiredMenuItems(
  menuItems: CarrotPlanFormOption<MenuItemOptionFields>[] | null | undefined
): CarrotPlanFormOption<MenuItemOptionFields>[] {
  return getMandatoryMenuItemsByFlag(menuItems, "isRequired")
}

// Helper: filters menu item options to the tier-prioritized ones that are not DISABLED.
export function getOnlyPrioritizedMenuItems(
  menuItems: CarrotPlanFormOption<MenuItemOptionFields>[] | null | undefined
): CarrotPlanFormOption<MenuItemOptionFields>[] {
  return getMandatoryMenuItemsByFlag(menuItems, "isPrioritized")
}

// Transforms a media item into the shape expected by the autocomplete form
export function getAutocompleteMediaItemOption(
  mediaItem: CarrotPlanMediaItem
): CarrotPlanFormOption<MediaOptionFields> {
  const mediaLink = buildMediaLink({
    fields: { label: mediaItem.title, slug: mediaItem.slug },
    categoryTitle: mediaItem.categoryTitle,
    mediaTypeId: mediaItem.contentType
  })
  return {
    label: mediaItem.title,
    value: mediaItem.entryId,
    optionMetadata: mediaItem.optionMetadata,
    optionFields: {
      link: mediaLink,
      entryId: mediaItem.entryId,
      slug: mediaItem.slug,
      categoryTitle: mediaItem.categoryTitle,
      contentType: mediaItem.contentType
    }
  }
}

// Transforms all given media items to the shape expected by the autocomplete
export function getAutocompleteMediaItemOptions(
  mediaItems: CarrotPlanMediaItem[]
): CarrotPlanFormOption<MediaOptionFields>[] {
  const mappedMediaOptions: CarrotPlanFormOption<MediaOptionFields>[] = []
  mediaItems?.forEach((mediaItem) => mappedMediaOptions.push(getAutocompleteMediaItemOption(mediaItem)))
  return mappedMediaOptions
}

// Transforms a topic option into the shape expected by the autocomplete form
export function getAutocompleteTopicOption(
  menuItemId: string,
  topicOption: CarrotPlanFormOption<TopicOptionFields>
): CarrotPlanFormOption<TopicOptionFields> {
  const topicLink = buildSubCategoryLink({
    fields: { label: topicOption.label, navTitle: topicOption.optionFields.navTitle },
    categoryTitle: topicOption.group
  })
  const mediaTypeId = getCarrotPlanMediaTypeIdForMenuItemId(menuItemId)?.toString() ?? ""
  const filteredMediaItems = getFilteredMediaItems(topicOption.optionFields.options, mediaTypeId)

  return {
    label: topicOption.label,
    group: topicOption.group,
    value: topicOption.value,
    optionMetadata: topicOption.optionMetadata,
    optionFields: {
      entityId: topicOption.optionFields.entityId,
      link: topicLink,
      navTitle: topicOption.optionFields.navTitle,
      titleWithCategory: null,
      options: filteredMediaItems
    }
  }
}

// Transforms ALL topic options into the shape expected by the autocomplete form (nested media items are NOT updated to media options)
// Also adds a disabled message to the options that are already added to the same plan and filters out any topics without media items
export function getAutocompleteTopicOptions(
  menuItemId: string,
  topicOptions: CarrotPlanFormOption<TopicOptionFields>[]
): CarrotPlanFormOption<TopicOptionFields>[] {
  if (topicOptions && carrotPlanMediaMenuItemIds.includes(menuItemId)) {
    const topicOptionsForAutocomplete = topicOptions.map((topicOption) =>
      getAutocompleteTopicOption(menuItemId, topicOption)
    )

    // filter out topics that don't have any options
    return topicOptionsForAutocomplete.filter((topic) => topic?.optionFields?.options.length > 0)
  }
  return []
}
